/**
 * #345 — the game card's "Open orders — this game" section.
 *
 * Every open order tied to THIS game — Kalshi moneyline + spread/total/period AND
 * Polymarket — with inline cancel on the Kalshi rows, so a trader can kill a resting
 * order straight from the game card instead of hunting for it in the positions tab
 * (StreamRift trading-safety asks #2 + #3; #45 "all open orders for a game").
 *
 * Presentation only. Which orders belong here, what each row says, and what a
 * cancel does are `useGameOpenOrdersModel`'s; read its header for the four
 * safety properties this surface depends on. Nothing below derives a second
 * opinion about any of them — in particular, the component is given
 * `isCancelPending` and `canBulkCancel` rather than the state they come from,
 * so it cannot decide on its own that an order is safe to destroy.
 *
 * Polymarket rows are display-only (no cancel) — the poly hook's cancel/replace exist
 * but wiring them here is out of scope. Renders nothing when there are no open orders.
 *
 * #1016 — the trader's own Polymarket US orders, across every US account they
 * can see, are rows here too and join "Cancel this game". Anything US the
 * sweep leaves out, or could not tie to a game, is named rather than hidden.
 *
 * #1472 — each US row has its own Cancel beside it, as a Kalshi row does: the
 * side rail's US control and path (retail and native), with the rail's
 * confirmation. A native order is never in "Cancel this game"; this is where
 * it is cancelled on the card.
 */
import { ConfirmModal, Modal } from '../../components/atoms/Modal';
import { EventStateWarning } from '../../components/atoms/EventStateWarning';
import { PassiveOperatorWarning } from '../../components/atoms/PassiveOperatorWarning';
import { useEventStateCertainty } from '@/hooks/ui/useEventStateCertainty';
import { buildGameCancelMessage } from './gameOpenOrderRows';
import { polyBadgeText, polyOutcomeLabel } from '@/lib/polymarket/marketTerms';
import { useGameOpenOrdersModel } from './useGameOpenOrdersModel';
import { useDeskOrderNotices } from '@/hooks/useDeskOrderNotices';
import { useOscarAccess } from '@/hooks/useOscarAccess';
import { kalshiEventPrefix, useOscarOvertakenNotices } from '@/hooks/useOscarBlockers';
import {
  goodToStartGameKey,
  goodToStartNoticeLine,
  goodToStartOrderLabel,
  useGoodToStartNotices,
} from '@/hooks/useGoodToStartNotices';
import type { OscarReleasePrefill } from './oscar';
import { MarketTypeChip, PinToggle } from '../../components/cards/cardKit';
import { useAuthStore } from '../../stores/authStore';
import { selectOwnedPinKeys, useTrackerPins } from '../../stores/trackerPins';
import type { GameRow } from './types';
import { describeUsGameNotices, type UsGameNotice } from '@/lib/polymarketUs/gameOpenOrders';
import { describeUsExclusions } from '../PositionTracker/usBulkCancelModel';
import { UsBulkCancelReport } from '../PositionTracker/UsBulkCancel';
import type { GameCancelConfirmation, GameUsOpenOrderRow } from './useGameOpenOrdersModel';
import type { UsRailCancelControl } from './useUsRailCancel';
import { useGameUsOpenOrders, type GameUsOpenOrders } from './useGameUsOpenOrders';
import { gameCancelBlockedAction } from './gameCancelBlockedAction';
import { VENUE_TONE } from '@/lib/venues';

const US_STATUS_HINT = 'Use Check status on the US orders in Position Tracker before cancelling again.';

type GameOpenOrdersProps = {
  game: GameRow;
  /** Arms the ticket's release-and-overtake from a resting oscar blocker
   *  row. NO order fires from here — arming is the human checkpoint; the
   *  trader sets the new size and fires from the ticket. */
  onOscarRelease?: (prefill: OscarReleasePrefill) => void;
};

/**
 * #1233 — `us` is the game's US read when the host owns it (Game Detail, which
 * shares it with the held snapshot). Without one, this list reads for itself.
 */
export function GameOpenOrders({ us, ...props }: GameOpenOrdersProps & { us?: GameUsOpenOrders }) {
  return us ? <GameOpenOrdersList {...props} us={us} /> : <SelfReadingGameOpenOrders {...props} />;
}

function SelfReadingGameOpenOrders(props: GameOpenOrdersProps) {
  const us = useGameUsOpenOrders(props.game.key);
  return <GameOpenOrdersList {...props} us={us} />;
}

function GameOpenOrdersList({
  game,
  onOscarRelease,
  us,
}: GameOpenOrdersProps & { us: GameUsOpenOrders }) {
  const {
    isEmpty,
    kalshiRows,
    polyRows,
    usRows,
    usStatus,
    usUnresolved,
    refreshUs,
    usBlockers,
    usReport,
    isCancelPending,
    timedOutNotices,
    cancelError,
    pendingRowCancel,
    requestRowCancel,
    confirmRowCancel,
    dismissRowCancel,
    pendingUsRowCancel,
    requestUsRowCancel,
    confirmUsRowCancel,
    dismissUsRowCancel,
    usCancelError,
    dismissUsCancelError,
    blockers,
    canBulkCancel,
    bulkRunning,
    bulkReport,
    pendingGameCancel,
    bulkCancelTitle,
    openGameCancel,
    confirmGameCancel,
    dismissGameCancel,
    retryFailedCancels,
  } = useGameOpenOrdersModel({ game, onOscarRelease, us });
  const pinViewerId = useAuthStore((s) => s.user?.id ?? null);
  const pinnedKeys = useTrackerPins((s) => selectOwnedPinKeys(s, pinViewerId));

  // #481 — how much this event's state can be believed. Read through the
  // same hook the order ticket uses so the two surfaces of one game card
  // can never contradict each other about the same event.
  const eventStateCertainty = useEventStateCertainty(
    game.gameState,
    game.gameStateAuthority,
    game.key,
  );

  // #472 owner signal — a KILLED blocker vanishes from the venue's open-order
  // list, so it has no row above; these ghost entries are the only way its
  // owner learns "overtaken by <teammate>" rather than seeing a silent
  // disappearance. Registry-backed, relay-attributed, auto-expires (~15 min).
  const oscarAccess = useOscarAccess();
  const overtakenNotices = useOscarOvertakenNotices({
    enabled: oscarAccess,
    tickerPrefixes: [
      ...new Set(
        [game.kalshi?.away?.marketTicker, game.kalshi?.home?.marketTicker]
          .map(kalshiEventPrefix)
          .filter((p): p is string => p != null),
      ),
    ],
  });

  // Good to Start watcher: an order the relay cancelled at kickoff because
  // Kalshi's start moved earlier than its expiry vanishes from the rows
  // below; this line says why, for every market of this game.
  const gtsNotices = useGoodToStartNotices(
    [game.kalshi?.away?.marketTicker, game.kalshi?.home?.marketTicker]
      .map(goodToStartGameKey)
      .filter((k): k is string => k != null),
  );

  const deskNotices = useDeskOrderNotices([game.kalshi?.away?.marketTicker,game.kalshi?.home?.marketTicker].filter((t): t is string => !!t));
  if (isEmpty && overtakenNotices.length === 0 && gtsNotices.length === 0 && deskNotices.length === 0) return null;

  return (
    <div data-testid="game-open-orders" className="border-border mt-3 rounded-lg border px-3 py-2.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs font-medium">Open orders — this game</span>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground/60 text-[10px]">cancel without leaving the card</span>
          <button
            onClick={openGameCancel}
            disabled={!canBulkCancel}
            title={bulkCancelTitle}
            className="shrink-0 rounded border border-red-500/50 px-2 py-0.5 text-[10px] font-semibold text-red-400 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {bulkRunning ? 'Cancelling…' : 'Cancel this game'}
          </button>
        </div>
      </div>
      {/* Above the rows and above the blockers: this is a statement about the
          EVENT the orders rest on, so it frames every row below it. It never
          disables a cancel — an uncertain event is a reason to make killing an
          order easier, never harder. Renders nothing when trusted. */}
      <EventStateWarning certainty={eventStateCertainty} className="mb-1.5" />
      {blockers.length + usBlockers.length > 0 && (
        // A confirmed refusal, not a gap in what we can see — the selection
        // KNOWS these orders cannot be scoped safely, so it earns `defect`.
        <PassiveOperatorWarning
          severity="defect"
          className="mb-1.5"
          // This card has never carried an icon, and #493 is a
          // behavior-preserving extraction — it does not get to change what a
          // trader sees on the way past. Preservation, not a style choice.
          showIcon={false}
          title="Cannot safely cancel this game"
          // Never "cancel the easy subset" — a partial sweep leaves exposure the
          // trader believes is gone. Name every reason instead. `break-all`
          // because these messages carry raw venue order IDs.
          explanation={[
            ...blockers.map((blocker, i) => (
              <div key={`${blocker.reason}-${blocker.orderId ?? i}`} className="break-all">
                {blocker.message}
              </div>
            )),
            ...usBlockers.map((blocker) => (
              <div key={`us-${blocker.accountId}-${blocker.intentId}`} className="break-all">
                {blocker.message}
              </div>
            )),
          ]}
          action={gameCancelBlockedAction(usBlockers.length > 0)}
        />
      )}
      <div className="space-y-1">
        {deskNotices.map(n => <div key={n.id} data-testid="desk-cancel-notice" className="rounded bg-amber-500/10 px-1.5 py-1 text-xs break-words">
          <strong>Order cancelled</strong> — {n.quantity} contracts on {n.ticker}, by {n.own ? 'you' : n.actorName ?? 'a teammate'} to resolve a crossed order. Order {n.orderId}. <time dateTime={n.createdAt}>{new Date(n.createdAt).toLocaleTimeString()}</time>
        </div>)}
        {gtsNotices.map((n) => (
          <div
            key={`gts-${n.kalshiOrderId}`}
            data-testid="gts-cancel-notice"
            className="flex items-start gap-1.5 rounded px-1.5 py-1 text-xs"
          >
            <span
              title="Good to Start: the relay cancelled this order when the game started, because the start moved earlier than the order's expiry"
              className="shrink-0 rounded bg-amber-500/20 px-1 py-0.5 text-[9px] font-bold text-amber-400"
            >
              GTS
            </span>
            {/* Wraps inside the full-width row rather than truncating: the
                reason is the information, so it never hides behind a hover. */}
            <span className="text-muted-foreground min-w-0 break-words">
              {goodToStartOrderLabel(n)} — {goodToStartNoticeLine(n)}
            </span>
          </div>
        ))}
        {overtakenNotices.map((n) => (
          <div
            key={n.kalshiOrderId}
            className="flex items-center gap-1.5 rounded px-1.5 py-1 text-xs opacity-75"
          >
            <span
              title={`This blocker was cleared by ${n.killedByClass === 'self' ? 'your sequence' : "a teammate's overtake"} — it is no longer resting`}
              className="shrink-0 rounded bg-amber-500/20 px-1 py-0.5 text-[9px] font-bold text-amber-400"
            >
              BLK
            </span>
            <span className="text-muted-foreground truncate">
              {n.blockerPriceCents}¢ {n.blockerSide.toUpperCase()} ×{n.count} — overtaken by{' '}
              {n.killedByClass === 'self' ? 'your sequence' : 'a teammate'}
            </span>
          </div>
        ))}
        {kalshiRows.map((row) => {
          // Disabled until the venue's own list stops carrying the ID. A
          // timed-out fence is deliberately NOT pending — the control comes back.
          const pending = isCancelPending(row.orderId);
          const releasePrefill = row.releasePrefill;
          return (
            <div
              key={row.orderId}
              className="hover:bg-foreground/5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded px-1.5 py-1 text-xs"
            >
              {/* #964: a crowded row (long bet + blocker controls) wraps its
                  pieces onto a second line instead of clipping the side badge
                  or the size @ price, which used to vanish behind the release
                  control. A row with room renders exactly as before. */}
              <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {row.isBlocker && (
                  <span
                    title="Oscar blocker — protects your queue level one price ahead"
                    className="shrink-0 rounded bg-amber-500/20 px-1 py-0.5 text-[9px] font-bold text-amber-400"
                  >
                    BLK
                  </span>
                )}
                {row.botTag && (
                  <span
                    title={row.botTag === 'PEN' ? "Penelope's order — placed for you by the bot; her Penelope tab says why it rests" : "The volatility harvester's order — placed for you by the bot"}
                    className="shrink-0 rounded bg-violet-500/20 px-1 py-0.5 text-[9px] font-bold text-violet-400"
                  >
                    {row.botTag}
                  </span>
                )}
                {/* #964: an NFL ladder prop's stat stacks under the one-line
                    badge, so the badge stays short and size @ price never clip. */}
                <span className="flex shrink-0 flex-col items-start gap-0.5">
                  <span
                    className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                      row.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}
                  >
                    {row.betTeam ? `(${row.betTeam}) ` : ''}
                    {row.side === 'yes' ? 'YES' : 'NO'}
                  </span>
                  <MarketTypeChip marketType={row.marketType} />
                </span>
                <span className="text-muted-foreground">{row.action}</span>
                <span className="text-foreground font-mono">
                  {row.remainingCount} @{' '}
                  {row.sidePriceCents != null ? `${row.sidePriceCents}¢` : '—'}
                </span>
              </div>
              <div className="ml-auto flex shrink-0 items-center gap-1">
                {/* #943 — the tracker's thumbtack: a pinned order is skipped by
                    Cancel All and Pull All. A plain button outside any
                    data-lt2-hotkey-control boundary, so it can never launch A/D. */}
                <PinToggle
                  pinned={pinnedKeys.has(row.orderId)}
                  protects
                  onToggle={() =>
                    useTrackerPins
                      .getState()
                      .toggle({ key: row.orderId, kind: 'order', label: row.label }, pinViewerId)
                  }
                />
                {releasePrefill && (
                  <button
                    onClick={() => onOscarRelease!(releasePrefill)}
                    disabled={pending}
                    title={
                      pending
                        ? 'Cancel pending — waiting for Open Orders'
                        : `Arm release & overtake: cancel this blocker, take ${releasePrefill.takePriceCents}¢ ${releasePrefill.takeSide.toUpperCase()}, then optionally attach a new blocker in the ticket`
                    }
                    className="rounded border border-amber-500/40 px-2 py-0.5 text-[10px] font-medium text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
                  >
                    Release &amp; overtake
                  </button>
                )}
                <button
                  onClick={() => requestRowCancel(row.orderId, row.label)}
                  disabled={pending}
                  title={pending ? 'Cancel pending — waiting for Open Orders' : 'Cancel order'}
                  aria-label={pending ? 'Cancel pending — waiting for Open Orders' : 'Cancel order'}
                  className="rounded border border-red-500/40 px-2 py-0.5 text-[10px] font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                >
                  {pending ? 'Canceling…' : 'Cancel'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {polyRows.length > 0 && (
        <div className="mt-1.5 space-y-1">
          <div className="text-muted-foreground/60 mb-0.5 text-[10px] font-medium uppercase tracking-wide">
            Polymarket · view-only
          </div>
          {polyRows.map((po) => {
            // #1450 (G1XL-1450-001) — the shared label policy decides whether
            // the chip names a team; a placeholder renders a plain side.
            const label = polyOutcomeLabel(po.betTeam);
            return (
              <div
                key={po.key}
                className="flex items-center justify-between gap-2 rounded px-1.5 py-1 text-xs"
              >
                <div className="flex items-center gap-1.5 overflow-hidden">
                  <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${VENUE_TONE.polymarket}`}>
                    POLY
                  </span>
                  {/* #1397: size @ price never wraps or shrinks. When a long outcome
                      name (soccer's full club names) runs out of room, only the
                      name ellipsizes; the side stays, and the title carries the
                      whole chip. The space before the side is a no-break space:
                      an ordinary one at a flex item's edge collapses away. */}
                  <span
                    className={`flex min-w-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                      po.side === 'no' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                    }`}
                    title={po.side ? polyBadgeText(label, po.side) : label ? `(${label})` : undefined}
                  >
                    {label && (
                      <>
                        (<span className="min-w-0 truncate">{label}</span>)
                      </>
                    )}
                    {po.side && (
                      <span className="shrink-0">
                        {label ? '\u00a0' : ''}
                        {po.side === 'no' ? 'NO' : 'YES'}
                      </span>
                    )}
                  </span>
                  {/* #1450: a spread or total's terms beside the badge. */}
                  <MarketTypeChip marketType={po.marketType} />
                  {po.action && <span className="text-muted-foreground">{po.action}</span>}
                  <span className="text-foreground shrink-0 whitespace-nowrap font-mono">
                    {po.sizeLabel} @ {po.priceCents != null ? `${po.priceCents}¢` : '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <UsOrderRows
        rows={usRows}
        loading={usStatus.loading}
        error={usStatus.error}
        stale={usStatus.stale}
        unresolved={usUnresolved}
        onRefresh={refreshUs}
        onCancel={requestUsRowCancel}
      />
      {bulkReport && (
        <div className="border-border mt-2 rounded border px-2 py-1.5">
          {/* "Submitted", never "cancelled": a 2xx DELETE is an accepted request.
              Removal is only proven when Open Orders stops listing the ID. */}
          <div className="text-[11px] font-medium">
            Cancellation submitted for {bulkReport.submitted.length} order
            {bulkReport.submitted.length === 1 ? '' : 's'}
            {bulkReport.failed.length > 0 ? ` · ${bulkReport.failed.length} failed` : ''}
            {bulkReport.alreadyPending.length > 0
              ? ` · ${bulkReport.alreadyPending.length} already pending`
              : ''}
            {bulkReport.snapshot.skippedPinned.length > 0
              ? ` · skipped ${bulkReport.snapshot.skippedPinned.length} pinned`
              : ''}
          </div>
          {bulkReport.failed.map((row) => (
            <div key={row.target.key} className="mt-0.5 text-[10px] text-red-400">
              {/* Full ID, wrapped — a truncated ID makes a failed order
                  indistinguishable from its siblings. */}
              <span className="font-semibold uppercase">{row.target.venue}</span>{' '}
              <span className="break-all font-mono">{row.target.orderId}</span>
              {row.error ? ` — ${row.error}` : ''}
            </div>
          ))}
          {bulkReport.alreadyPending.map((row) => (
            <div key={row.target.key} className="text-muted-foreground mt-0.5 text-[10px]">
              <span className="font-semibold uppercase">{row.target.venue}</span>{' '}
              <span className="break-all font-mono">{row.target.orderId}</span> — {row.error}
            </div>
          ))}
          {bulkReport.refreshErrors.map((message) => (
            <div key={message} className="mt-0.5 text-[10px] text-amber-400">
              Requests were sent, but the order list could not be refreshed — {message}
            </div>
          ))}
          {bulkReport.failed.length > 0 && (
            <button
              onClick={retryFailedCancels}
              disabled={bulkRunning}
              className="mt-1 rounded border border-red-500/40 px-2 py-0.5 text-[10px] font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-50"
            >
              Retry failed
            </button>
          )}
        </div>
      )}
      {usReport && (
        <div className="px-2 text-[11px]">
          <UsBulkCancelReport results={usReport.results} note={usReport.note} statusHint={US_STATUS_HINT} />
        </div>
      )}
      {cancelError && <div className="mt-1.5 text-[10px] text-red-400">{cancelError}</div>}
      {usCancelError && (
        <div className="mt-1.5 flex items-start justify-between gap-2 text-[10px] text-red-400" data-testid="game-us-cancel-error">
          <span className="min-w-0 [overflow-wrap:anywhere]">{usCancelError}</span>
          <button
            type="button"
            onClick={dismissUsCancelError}
            className="shrink-0 text-red-300/70 hover:text-red-200"
            aria-label="Dismiss Polymarket US cancel error"
          >
            Dismiss
          </button>
        </div>
      )}
      {/* Rendered independently of cancelError so an immediate failure and a
          synchronization timeout can't hide each other (#241). */}
      {timedOutNotices.map(({ orderId, message }) => (
        <div key={`timeout-${orderId}`} className="mt-1.5 text-[10px] text-amber-400">
          {orderId}: {message}
        </div>
      ))}
      <ConfirmModal
        isOpen={pendingRowCancel !== null}
        onClose={dismissRowCancel}
        onConfirm={confirmRowCancel}
        title="Cancel order?"
        message={pendingRowCancel ? `Cancel ${pendingRowCancel.label}?` : 'Cancel this order?'}
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />
      {/* #1472 — US has its own confirmation, as on the side rail, so a US
          target can never reach the Kalshi cancel. */}
      <ConfirmModal
        isOpen={!!pendingUsRowCancel}
        onClose={dismissUsRowCancel}
        onConfirm={confirmUsRowCancel}
        title="Cancel Polymarket US order?"
        message={pendingUsRowCancel?.message ?? 'Cancel this order?'}
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />
      {/* The per-game confirmation renders the FROZEN set — count, venues and
          every full order ID — so the trader approves an exact list, not a
          moving one. Modal's body scrolls (max-h-60vh) while the footer stays
          reachable, which is what keeps the short viewport usable. */}
      <Modal
        isOpen={pendingGameCancel !== null}
        onClose={dismissGameCancel}
        title="Cancel this game?"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={dismissGameCancel}
              className="bg-muted hover:bg-muted/80 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            >
              Back
            </button>
            <button
              onClick={confirmGameCancel}
              disabled={bulkRunning}
              className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:opacity-50"
            >
              Cancel {confirmCount(pendingGameCancel)} order
              {confirmCount(pendingGameCancel) === 1 ? '' : 's'}
            </button>
          </div>
        }
      >
        {pendingGameCancel && (
          <div>
            <p className="text-muted-foreground text-sm">
              {buildGameCancelMessage(pendingGameCancel.kp, usTargetsOf(pendingGameCancel).length)}
            </p>
            <ul className="mt-2 space-y-0.5">
              {pendingGameCancel.kp.targets.map((target) => (
                <li key={target.key} className="text-xs">
                  <span className="text-muted-foreground font-semibold uppercase">
                    {target.venue}
                  </span>{' '}
                  <span className="text-foreground break-all font-mono">{target.orderId}</span>
                </li>
              ))}
            </ul>
            <UsConfirmLines confirmation={pendingGameCancel} />
          </div>
        )}
      </Modal>
    </div>
  );
}

const usTargetsOf = (c: GameCancelConfirmation | null) => (c?.us.phase === 'frozen' ? c.us.inventory.targets : []);
const confirmCount = (c: GameCancelConfirmation | null) => (c ? c.kp.targets.length + usTargetsOf(c).length : 0);

/** #1114 T8 quick reference on the US section's Refresh / Retry. */
const US_REFRESH_TITLE = 'Polymarket US orders are read, not streamed; click to read them again.';

/** #1472 — a US row's cancel, in the Kalshi row's place and button style; its
 *  states and words are the side rail's (`usRailCancelControl`). */
function UsRowCancel({ control, onCancel }: { control: UsRailCancelControl; onCancel: () => void }) {
  if (control.kind === 'none') return null;
  if (control.kind === 'status') {
    return (
      <span
        data-testid="game-us-cancel-status"
        title={control.title}
        className={`text-[10px] font-medium ${
          control.unconfirmed ? 'text-amber-400 light:text-amber-700' : 'text-muted-foreground'
        }`}
      >
        {control.label}
      </span>
    );
  }
  return (
    <button
      type="button"
      data-testid="game-us-cancel"
      onClick={onCancel}
      title={control.title}
      aria-label={control.title}
      className="rounded border border-red-500/40 px-2 py-0.5 text-[10px] font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-50"
    >
      {control.label ?? 'Cancel'}
    </button>
  );
}

/** US rows, their read state, and what the sweep leaves out. */
function UsOrderRows({
  rows,
  loading,
  error,
  stale,
  unresolved,
  onRefresh,
  onCancel,
}: {
  rows: readonly GameUsOpenOrderRow[];
  loading: boolean;
  error: string | null;
  stale: { code: string; readAtMs: number } | null;
  unresolved: readonly UsGameNotice[];
  onRefresh: () => void;
  onCancel: (key: string) => void;
}) {
  if (rows.length === 0 && !error && !stale && unresolved.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1" data-testid="game-open-orders-us">
      <div className="text-muted-foreground/60 mb-0.5 flex items-center justify-between gap-2 text-[10px] font-medium uppercase tracking-wide">
        <span>Polymarket US · all your accounts</span>
        <button
          onClick={onRefresh}
          disabled={loading}
          title={US_REFRESH_TITLE}
          className="normal-case tracking-normal hover:text-foreground disabled:opacity-50"
        >
          {loading ? 'Reading…' : error || stale ? 'Retry' : 'Refresh'}
        </button>
      </div>
      {error && (
        <div className="px-1.5 text-[10px] text-amber-400">
          Polymarket US orders could not be read (<span className="font-mono">{error}</span>) — none are shown or included.
        </div>
      )}
      {stale && (
        <div
          className="px-1.5 text-[10px] text-amber-400 light:text-amber-700"
          data-testid="game-open-orders-us-stale"
          title={stale.code}
        >
          Polymarket US orders unavailable — showing the last reading.
          <span className="block">Last read {new Date(stale.readAtMs).toLocaleTimeString()} · excluded from Cancel this game.</span>
        </div>
      )}
      {rows.map((row) => (
        <div key={row.key} className="rounded px-1.5 py-1 text-xs">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${VENUE_TONE.polymarket_us}`}>US</span>
              <span
                className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                  row.outcome === 'no' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                }`}
              >
                {row.outcome === 'no' ? 'NO' : 'YES'}
              </span>
              <span className="text-muted-foreground">{row.action}</span>
              <span className="text-foreground truncate font-mono">
                {row.remaining} @ {row.priceCents}¢
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <span className="text-muted-foreground font-mono text-[10px]" title="Polymarket US account">
                …{row.accountTail}
              </span>
              <UsRowCancel control={row.cancel} onCancel={() => onCancel(row.key)} />
            </div>
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2 text-[10px]">
            <span className="text-muted-foreground min-w-0 truncate" title={row.label}>{row.label}</span>
            <span className={`shrink-0 ${row.note ? 'text-amber-400' : 'text-muted-foreground'}`}>{row.note ?? row.status}</span>
          </div>
        </div>
      ))}
      {/* Rows carry their own reason; only orders with no row are listed here. */}
      {describeUsGameNotices(unresolved, 'not matched to a game').map((line) => (
        <div key={line} className="px-1.5 text-[10px] text-amber-400">{line}</div>
      ))}
    </div>
  );
}

/** The frozen US part of the confirmation: what is sent, what is left out and why. */
function UsConfirmLines({ confirmation }: { confirmation: GameCancelConfirmation }) {
  const us = confirmation.us;
  const box = 'mt-2 space-y-0.5 text-xs';
  if (us.phase === 'off') return null;
  if (us.phase === 'omitted') {
    return (
      <div className={box} data-testid="us-game-lines">
        <p className="text-amber-300 light:text-amber-700">
          {us.loading
            ? 'Polymarket US is still being read, so no US orders are included. Go back and reopen Cancel this game once it finishes to include them.'
            : `Polymarket US orders could not be read (${us.code}), so none are included.`}
        </p>
      </div>
    );
  }
  const { targets, excluded, accounts } = us.inventory;
  return (
    <div className={box} data-testid="us-game-lines">
      <p className="text-foreground">
        {targets.length > 0
          ? `Polymarket US: ${targets.length} order${targets.length === 1 ? '' : 's'} across ${accounts} account${accounts === 1 ? '' : 's'}, sent one at a time.`
          : 'Polymarket US: none of your orders for this game can be cancelled here.'}
      </p>
      <p className="text-muted-foreground">
        Your orders only, in every Polymarket US account you can see — teammates’ orders are not included. Listed from G1’s saved records; orders placed outside G1 are not listed.
      </p>
      {[...describeUsExclusions(excluded), ...describeUsGameNotices(us.unresolved, 'not matched to a game')].map((line) => (
        <p key={line} className="text-amber-300 light:text-amber-700">{line}</p>
      ))}
      {targets.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {targets.map((t) => (
            <li key={t.key}>
              <span className="text-muted-foreground font-semibold">US</span>{' '}
              <span className="text-foreground break-all font-mono">{t.orderId}</span>{' '}
              <span className="text-muted-foreground break-all">{t.label}</span>
              <span className="text-muted-foreground whitespace-nowrap"> (account …{t.accountId.slice(-4)})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
